/**
 * S06 — FOOD NOIR: Visual Aesthetic & Character Archetype
 *
 * Three-pillar breakdown of the visual direction: Food Noir aesthetic
 * (Refn / Leon the Professional), Hero's Journey (violence → business),
 * Against All Odds (showing and proving). Atmospheric background layer
 * pulls from HF#4 (dish-plated under neon) at low opacity.
 */

import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { MonumentType } from "@/components/primitives/MonumentType";
import { sectionCopy, foodNoirPillars } from "@/lib/voice";
import dishNeonSrc from "@/assets/higgsfield/hf-04-dish-plated.webp";
import { keyImages, moodboardUrl } from "@/lib/moodboard";

gsap.registerPlugin(ScrollTrigger);

export function S06_TheDish() {
  const copy = sectionCopy.s06;
  const sectionRef = useRef<HTMLElement | null>(null);
  const pillarRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const ctx = gsap.context(() => {
      pillarRefs.current.forEach((p) => {
        if (!p) return;
        gsap.fromTo(
          p,
          { opacity: 0, y: 40 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            ease: "power3.out",
            scrollTrigger: {
              trigger: p,
              start: "top 85%",
              end: "top 55%",
              scrub: 1,
            },
          },
        );
      });
    }, el);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="s06"
      className="section"
      style={{
        backgroundColor: "var(--ink)",
        color: "var(--bone)",
        flexDirection: "column",
        padding: "14vh var(--space-lg) 12vh",
      }}
    >
      {/* Atmospheric background — HF#4 neon kitchen dish */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `url(${dishNeonSrc})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "brightness(0.38) contrast(1.15) saturate(0.85)",
          zIndex: 0,
        }}
      />
      {/* Secondary atmospheric layer — Kitano reference */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `url(${moodboardUrl(keyImages.kitano)})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "grayscale(100%) contrast(1.4) brightness(0.3)",
          mixBlendMode: "multiply",
          opacity: 0.4,
          zIndex: 0,
        }}
      />
      {/* Vignette */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse at center, rgba(5,5,5,0.4) 0%, rgba(5,5,5,0.92) 100%)",
          pointerEvents: "none",
          zIndex: 1,
        }}
      />
      {/* Cobalt spot wash */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(135deg, var(--cobalt) 0%, transparent 55%)",
          mixBlendMode: "multiply",
          opacity: 0.22,
          pointerEvents: "none",
          zIndex: 2,
        }}
      />

      <MonumentType word={copy.monument} theme="ink" />

      {/* Header */}
      <div
        style={{
          position: "relative",
          zIndex: 3,
          textAlign: "center",
          marginBottom: "var(--space-xl)",
        }}
      >
        <div
          className="type-meta-sm"
          style={{
            color: "var(--cobalt)",
            marginBottom: "var(--space-md)",
            letterSpacing: "var(--ls-meta-wide)",
          }}
        >
          {copy.meta}
        </div>
        <h2
          className="type-hero-lg"
          style={{
            color: "var(--bone)",
            margin: 0,
            textShadow: "0 4px 40px rgba(0,0,0,0.95)",
          }}
        >
          {copy.hero}
        </h2>
        {copy.subhero && (
          <p
            className="type-voice"
            style={{
              color: "var(--fog)",
              marginTop: "var(--space-md)",
              fontSize: "clamp(1.125rem, 1.4vw, 1.5rem)",
              fontStyle: "italic",
              maxWidth: "60ch",
              margin: "var(--space-md) auto 0",
            }}
          >
            The project uses a stylized, gritty approach to storytelling.
          </p>
        )}
      </div>

      {/* Three-pillar breakdown */}
      <div
        className="s06-pillars"
        style={{
          position: "relative",
          zIndex: 3,
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "var(--space-lg)",
          width: "min(1400px, 92vw)",
          margin: "0 auto",
        }}
      >
        {foodNoirPillars.map((pillar, i) => (
          <div
            key={pillar.title}
            ref={(el) => {
              pillarRefs.current[i] = el;
            }}
            style={{
              padding: "var(--space-lg) var(--space-md)",
              border: "1px solid rgba(245, 240, 228, 0.1)",
              background: "rgba(5,5,5,0.3)",
              backdropFilter: "blur(4px)",
            }}
          >
            <div
              className="type-meta-sm"
              style={{
                color: "var(--cobalt)",
                marginBottom: "var(--space-md)",
                letterSpacing: "var(--ls-meta-wide)",
              }}
            >
              / 0{i + 1}
            </div>
            <h3
              style={{
                fontFamily: "var(--f-serif-display)",
                fontWeight: 900,
                fontStretch: "75%",
                fontSize: "clamp(1.5rem, 2.2vw, 2.25rem)",
                lineHeight: 1,
                letterSpacing: "-0.01em",
                color: "var(--bone)",
                textTransform: "uppercase",
                margin: 0,
                marginBottom: "var(--space-md)",
                textShadow: "0 2px 16px rgba(0,0,0,0.9)",
              }}
            >
              {pillar.title}
            </h3>
            <p
              style={{
                fontFamily: "var(--f-serif-body)",
                fontSize: "clamp(0.9375rem, 1.05vw, 1.0625rem)",
                lineHeight: 1.55,
                color: "var(--bone)",
                opacity: 0.88,
                margin: 0,
              }}
            >
              {pillar.body}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}
